/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2017 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* global globalThis */
/**
 * @file
 */

import { loadScript } from '../core/DomUtil';
import { logging } from './LoggingAPI';
import { router } from './RouterAPI';
import { setEnvVars, isProd } from '../core/EnvUtil';
import loadExtras from './loadExtras';
import MethodOverrideManager from '../core/MethodOverrideManager';

const LOCALHOST = '//local-test.acrobat.com';
const EPHEMERALHOST = `//${globalThis.location?.host ?? 'stage.acrobat.adobe.com'}/ephemeral`;

const origin = window.adobe_dc_sdk && window.adobe_dc_sdk.launchOptions && window.adobe_dc_sdk.launchOptions.server || window.location.origin;

const serverLogger = logging.getLogger('dc-discovery', { enableStream: 'server' });
const clientLogger = logging.getLogger('dc-discovery', { enableStream: 'console' });

const classesLoaded = [];
let classesLogged = [];
// log a list of all the dropin and provider classes that have been loaded
const logClasses = flush => {
  // derive the list of the dropins/providers that have not been logged yet
  const unlogged = classesLoaded.filter(c => !classesLogged.includes(c));
  if (unlogged.length) serverLogger.emit('dc-discovery-dropins', { loaded_dropins: unlogged.join(',') });
  // update the logged classes with a copy of the classesLoaded
  classesLogged = classesLoaded.slice();
  // @ts-ignore
  if (flush) logging.constructor.flushAll(serverLogger.config);
};
window.addEventListener('load', logClasses.bind(null, false));
window.addEventListener('beforeunload', logClasses.bind(null, true));

/**
 * Extract the protocol://server.domain/path/ URI from the dropin.url.
 * This gives the dropin the proper relative path for further lazy loading.
 */
function getPublicPath(url) {
  return url.replace(/^([\w/:\-.]+\/)\S*/, '$1');
}

const absoluteURL = url => {
  if (!/^(https?:|file:)?\/\//.test(url)) {
    // If the dropin doesn't have an absolute URL, create an absolute URL
    // by adding the domain where discovery is located.
    return origin + url;
  }
  return url;
};

const useES6 = () => {
  // for now, just Chrome.  (although this check might also find edge-based chrome)
  const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
  // performance.timeOrigin has been supported since Chrome version 62
  return isChrome && window.performance && window.performance.timeOrigin;
};

const expandURL = (ver, dep, asset) => {
  const es6Substitute = useES6() ? '-es6' : '';
  const url = asset.url_template
    .replace(/{DEPLOYMENT}/, dep)
    .replace(/{VERSION}/, ver)
    .replace(/{ES6}/, es6Substitute);

  asset.url_template = absoluteURL(url);
};

/**
 * @description
 * this is an over-simplified variation of the _.merge() in lodash
 * @method
 * @param {object} a - The object into which you merge another object
 * @param {object} b - The object which is to be merged.
 * @returns {object} - The object a after being merged with object b
 * @private
 */
function merge(a, b) {
  if (!b) {
    return a;
  }
  Object.keys(b).forEach(key => {
    if (typeof a[key] === 'object'
            && a[key]
            && !Array.isArray(a[key])
            && typeof b[key] === 'object'
            && b[key] !== null
    ) {
      a[key] = merge(a[key], b[key]);
    } else {
      a[key] = b[key];
    }
  });
  return a;
}

/**
 * @class
 */
export class Discovery {
  dropins;
  providers;
  groups;
  _manifestPromise;
  _mobxPromise;
  _spectrumV3CorePromise;
  usingCache;
  overrides;
  pinnedRanges;
  unpinnedError;

  constructor() {
    this.dropins = {};
    this.providers = {};
    this.groups = {};
    this._manifestPromise = null;
    this._mobxPromise = null;
    this._spectrumV3CorePromise = null;

    window.adobe_dc_sdk.info = () => {
      const info: AdobeSDKInfo = {
        'dc-core': CORE_PACKAGE.version,
        dropins: {},
        providers: {},
        'react-spectrum': CORE_PACKAGE.react_spectrum,
        floodgate: [],
      };
      Object.keys(this.dropins || []).sort().forEach(dropin => {
        info.dropins[dropin] = this.dropins[dropin].version;
      });
      Object.keys(this.providers || []).sort().forEach(provider => {
        info.providers[provider] = this.providers[provider].version;
      });
      if (window.adobe_dc_sdk.limitsInfo) {
        info.limits = window.adobe_dc_sdk.limitsInfo();
      }
      if (window.adobe_dc_sdk.getFeatureFlags) {
        info.floodgate = window.adobe_dc_sdk.getFeatureFlags();
      }
      return info;
    };
  }

  /**
   * @description
   * Look for, and apply local overrides
   * @method
   * @param {object} group - The group of dropins & providers to apply override for
   * @returns {string} url - The url with local host override applied.
   * @private
   */
  _applyOverrides(group) {
    let url = group.url_template;
    const queryParams = router.queryParams;
    if (!isProd()) {
      ['dropins', 'providers'].forEach(category => {
        /* eslint guard-for-in: 0 */
        Object.keys(group[category] || []).forEach(id => {
          const testURL = queryParams[`dropin!${id}`] || queryParams[`provider!${id}`];
          if (testURL) {
            url = testURL.replace(/^local/, LOCALHOST);
            if (url.match(/^ephemeral:/)) {
              url = url.replace(/^ephemeral:/, `${EPHEMERALHOST}/`);
            }
          }
        });
      });
    }
    return url;
  }

  _addComponents(group, componentType, finalURL, expandTemplate, version, hasFinalVersion, isPinned) {
    // gather the set of dropins that match this version
    Object.keys(group[componentType] || []).forEach(key => {
      const component = Object.assign(this[componentType][key] || {}, group[componentType][key]);
      if (component.assets) {
        component.assets.forEach(expandTemplate);
      }
      // Assign the url from the best version match
      component.version = version;
      component.hasFinalVersion = hasFinalVersion;
      component.isPinned = isPinned;
      component.url = finalURL;
      component.public_path = getPublicPath(component.url);
      // Add a component property that will be used to correlate dropins/providers
      // that are grouped in the same bootstrap
      component.component = group.component;
      this[componentType][key] = component;
    });
  }

  /**
   * @description
   * If the host app has cached versionManifest, preprocess it here.
   * @method
   * @private
   */
  _findCachedDropins() {
    this.usingCache = true;
    let grouping;
    const vars = {};
    const components = merge(window.adobe_dc_sdk.cache.version_manifest, this.overrides);

    // This components list comes via our version manifest
    // eslint-disable-next-line
    for (grouping in components) {
      // dropinharness can place a dc-discovery override here.
      // ignore it.
      if (grouping === 'dc-discovery') {
        // eslint-disable-next-line no-continue
        continue;
      }
      try {
        const group = components[grouping];
        const version = `${group.pinnedCore}_${group.pinnedVersion}`;
        const expandTemplate = expandURL.bind(null, version, grouping);
        expandTemplate(group);
        const finalURL = this._applyOverrides(group);
        const assets = group.assets || [];
        assets.forEach(expandTemplate);
        const pinnedRange = this.pinnedRanges && this.pinnedRanges[group.component];
        // if it's a trivial range, indicate that the runtime version manifest is not needed
        const hasFinalVersion = !!(pinnedRange && /^\d+\.\d+\.\d+$/.test(pinnedRange));
        this.groups[group.component] = {
          // assume all dropins are dependent on dc-extras and dc-mobx unless they declare otherwise
          assets, dependencies: group.dependencies || ['dc-extras', 'dc-mobx'],
        };
        this._addComponents(group, 'dropins', finalURL, expandTemplate, group.pinnedVersion, hasFinalVersion, !!pinnedRange);
        this._addComponents(group, 'providers', finalURL, expandTemplate, group.pinnedVersion, hasFinalVersion, !!pinnedRange);

        Object.keys(group.providers || []).forEach(key => {
          // make a copy of any provider vars to be used later by EnvUtil
          Object.assign(vars, group.providers[key]);
        });
        Object.keys(group.dropins || []).forEach(key => {
          // make a copy of any dropin vars to be used later by EnvUtil
          Object.assign(vars, group.dropins[key]);
        });
      } catch (err) {
        // Make sure one error doesn't abort all discovery processing -- just the current group
        clientLogger.error(`Error: "${err.toString()}" for component: ${grouping}`);
      }
    }
    setEnvVars(vars);
  }

  /**
   * @method
   * @param {object} inputOverrides - The overrides config to be applied
   * @param {object} inputPinnedRanges - The set of pinned ranges to be used
   * @public
   */
  ready(inputOverrides, inputPinnedRanges) {
    if (window.performance && window.performance.mark) {
      window.performance.mark(`es6-${useES6() ? 'on' : 'off'}`);
    }
    this.overrides = { ...inputOverrides };
    this.pinnedRanges = inputPinnedRanges;
    serverLogger.emit('Discovery._loadVersions()', {
      'dc-core-version': CORE_PACKAGE.version,
      'dc-core-url': window.location.origin,
    });

    let versions;
    if (!isProd()) {
      // don't allow query parameters in prod
      versions = router.queryParams['app!versions'];
    }
    // if versions have been provided as both query parameters and session storage,
    // query parameters take priority
    try {
      versions = versions || (window.sessionStorage ? window.sessionStorage.getItem('app!versions') : null);
    } catch (e) {
      // Looks like sessionStorage is not enabled.. Not logging it.
    }

    try {
      if (versions === 'latest') {
        this.pinnedRanges = null;
      } else if (versions) {
        // convert home2'1.921.0!files'0.2.5 = ["home2'1.921.0", "files'0.2.5"]
        this.pinnedRanges = versions.split('!').reduce((obj, o) => {
          // convert "home2'1.921.0" to k="home2", v="1.921.0"
          const [k, v] = o.split("'");
          // convert k="home2", v="1.921.0" to {"home2: "1.921.0"}
          obj[k] = v;
          return obj;
        }, {});
      }
    } catch (e) {
      clientLogger.warn(`invalid versions: ${versions}`);
    }
    // double check that our pinned versions are for the current version
    // of dc-core
    if (this.pinnedRanges
          && this.pinnedRanges['dc-core']
          && this.pinnedRanges['dc-core'] !== CORE_PACKAGE.version) {
      const msg = `pinned dc-core (${this.pinnedRanges['dc-core']}) doesn't match ${CORE_PACKAGE.version}`;
      clientLogger.error(msg);
      // continue without version pinning
      this.pinnedRanges = null;
    }
    // The !versions qualifier is here so that in the case where we have pinned versions specified
    // via version parameter, we will load versionManifest to resolve the pinned versions to the correct
    // version of dc-core
    if (!versions && this.pinnedRanges && window.adobe_dc_sdk.cache.version_manifest) {
      this._findCachedDropins();
      return Promise.resolve();
    }
    return this._loadVersions();
  }

  _loadVersions() {
    const dcCorePath = window.adobe_dc_sdk.launchOptions.dc_core_path;
    return loadScript(`${dcCorePath}/dc-discovery-versions.js`).then(() => {
      const discoveryVersions = window.adobe_dc_sdk['dc-discovery-versions'];
      const dcCore = { setEnvVars, loadScript, logging };
      return discoveryVersions.default(this, origin, clientLogger, expandURL, merge, dcCore);
    });
  }

  /**
   * @description
   * Load the bootstrap module of a dropin
   * @method
   * @param {string} id -- the id of the dropin used to register it in package.json
   * @returns {Promise} - a promise object that resolves to the bootstrap interface of this dropin.
   * @public
   */
  loadDropin(id) {
    // TODO: Move verb check to loadVerb method once caller uses loadVerb
    return this._loadBootstrap(id, 'dropins').then((verb: Awaited<IBootstrap>) => (
      verb.bootstrap && verb.bootstrap.loadVerb
        ? verb.bootstrap.loadVerb(verb.public_path).then(bootstrap => ({ ...verb, bootstrap }))
        : verb
    ));
  }

  /**
   * @description
   * Load the bootstrap object of a verb
   * @method
   * @param {string} id -- the id of the verb used to register it in package.json
   * @param {string} category -- dropins or providers
   * @returns {Promise} - a promise object that resolves to the bootstrap interface of this verb.
   * @public
   */
  loadVerb(id, category = 'dropins') {
    return this._loadBootstrap(id, category).then((verb: Awaited<IDropin>) => (
      verb.bootstrap && verb.bootstrap.loadVerb
        ? verb.bootstrap.loadVerb(verb.public_path).then(bootstrap => ({ ...verb, bootstrap }))
        : verb
    ));
  }

  /**
   * @description
   * Load the bootstrap module of a provider
   * @method
   * @param {string} id -- the id of the provider used to register it in package.json
   * @returns {Promise} - a promise object that resolves to the bootstrap interface of this provider.
   * @public
   */
  loadProvider(id) {
    return this._loadBootstrap(id, 'providers');
  }

  registerMethodOverride = async (id, methodName, override) => {
    const category = this.dropins[id] ? 'dropins' : 'providers';

    try {
      await this._loadBootstrap(id, category);
    } catch (error) {
      clientLogger.error(`Error: not able to register method override for "${methodName}"`);
      return null;
    }

    const component = this[category][id];
    const bootstrap = component?.bootstrap;

    if (!bootstrap || !bootstrap[methodName]) {
      return null;
    }

    // if method has not registered with override
    if (!bootstrap[`${methodName}_overrideManager`]) {
      // backup the original method
      bootstrap[`_${methodName}`] = bootstrap[methodName];
      // create a new MethodOverrideManager instance
      bootstrap[`${methodName}_overrideManager`] = new MethodOverrideManager(methodName, bootstrap);
      // point method reference to override executor
      bootstrap[methodName] = (...args) => bootstrap[`${methodName}_overrideManager`].execute(...args);
    }
    // apply override
    const overrideId = bootstrap[`${methodName}_overrideManager`].use(override);

    return overrideId;
  };

  unregisterMethodOverride = async (id, methodName, overrideId) => {
    const category = this.dropins[id] ? 'dropins' : 'providers';

    try {
      await this._loadBootstrap(id, category);
    } catch (error) {
      clientLogger.error(`Error: not able to unregister ${overrideId} for "${methodName}"`);
      return false;
    }

    const component = this[category][id];
    const bootstrap = component?.bootstrap;

    if (!bootstrap) {
      return false;
    }

    if (bootstrap[`${methodName}_overrideManager`]) {
      return bootstrap[`${methodName}_overrideManager`].remove(overrideId);
    }

    return false;
  };

  /**
   * @description
   * Calls a method exposed in bootstrap. Loads bootstrap if not already loaded.
   * @method
   * @param {string} id -- the id of component used to register it in package.json
   * @param {string} category -- category of component (dropins/providers)
   * @param {string} methodName -- name of the method to be called on the component
   * @param {array} ...args -- Array of arguments to be passed to the method
   * @returns {Promise} - a promise object that resolves to the value returned by the method.
   * @public
   */
  callBootstrapMethod(id, methodName, ...args) {
    const category = this.dropins[id] ? 'dropins' : 'providers';
    return this.loadVerb(id, category).then((component: Awaited<IDropin>) => {
      if (component && component.bootstrap) {
        if (component.bootstrap[methodName]) {
          return component.bootstrap[methodName](...args);
        }
      }
      return Promise.reject(new ReferenceError(`Could not execute ${id}.${methodName}`));
    });
  }

  /**
   * @description
   * Loads the bootstrap/bootstrapy module of the dropin.
   * @method
   * @param {string} id -- the id of component to load the boostrap for
   * @param {string} category - Category of the component to load bootstrap.
   *  category is either: "dropins" or "providers"
   * @returns {Promise} - promise that resolves to load boostrap file on success
   *  Or error on failure.
   * @private
   */
  _loadBootstrap(id, category): any {
    const self = this;
    const object: IDropin = self[category][id];
    if (!object) {
      return Promise.reject(new TypeError(`UNKNOWN_COMPONENT '${id}'`));
    }
    let manifestPromise;
    let extrasPromise;
    let mobxPromise;
    let spectrumV3CorePromise;
    const dependencies = (object.dependencies || []).concat(self.groups[object.component].dependencies);
    let allowUnpinnedComponents = false;
    if (window.adobe_dc_sdk.launchOptions.allowUnpinnedComponents) {
      allowUnpinnedComponents = window.adobe_dc_sdk.launchOptions.allowUnpinnedComponents;
    }
    if (dependencies.includes('dc-extras')) {
      loadExtras();
      extrasPromise = window.adobe_dc_sdk.launchOptions.loadExtrasPromise;
    } else {
      extrasPromise = Promise.resolve();
    }
    if (dependencies.includes('dc-mobx')) {
      if (!this._mobxPromise) {
        const mobxJsURI = window.adobe_dc_sdk.launchOptions.dc_mobx_js_uri;
        this._mobxPromise = loadScript(mobxJsURI).then(() => {
          // execute the mobx index file
          window.adobe_dc_sdk['dc-mobx'](window.adobe_dc_sdk.aliases['dc-mobx']);
        });
      }
      mobxPromise = this._mobxPromise;
    } else {
      mobxPromise = Promise.resolve();
    }

    if (dependencies.includes('dc-spectrum-v2-dark')) {
      if (!document.getElementById('dc-spectrum-v2-dark-css-id')) {
        const link = document.createElement('link');
        link.id = 'dc-spectrum-v2-dark-css-id';
        link.href = window.adobe_dc_sdk.launchOptions.dc_spectrum_v2_dark_css_uri;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
    }

    if (dependencies.includes('dc-spectrum-v3-core')) {
      if (!this._spectrumV3CorePromise) {
        const spectrumURI = window.adobe_dc_sdk.launchOptions.dc_spectrum_v3_core_js_uri;
        this._spectrumV3CorePromise = loadScript(spectrumURI);
        if (!document.getElementById('dc-spectrum-v3-core-css-id')) {
          const link = document.createElement('link');
          link.id = 'dc-spectrum-v3-core-css-id';
          link.href = window.adobe_dc_sdk.launchOptions.dc_spectrum_v3_core_css_uri;
          link.rel = 'stylesheet';
          document.head.appendChild(link);
        }
      }
      spectrumV3CorePromise = this._spectrumV3CorePromise;
    } else {
      spectrumV3CorePromise = Promise.resolve();
    }

    // If we're using the cache, we insist that all components we load must be pinned.
    // i.e. we won't load versionManifest to discover unpinned dropins/providers
    // (also don't execute this check when we're running in the dropin harness)
    if (!isProd() && this.usingCache && !object.isPinned && !window.DropinHarness && !allowUnpinnedComponents) {
      const msg = `Cannot load unpinned component: ${id}`;
      // We want the 'unpinned' error to be fatal for our app.
      // Unfortunately, this error is often caught/ignored.
      // Adding an alert() should be enough to fail automation tests
      // eslint-disable-next-line no-alert
      if (!this.unpinnedError) window.alert(msg);
      // emit the alert only once
      this.unpinnedError = true;
      throw new Error(msg);
    }
    if (object.hasFinalVersion) {
      // we're ok with the version information in-hand
      manifestPromise = Promise.resolve();
    } else {
      // need to ensure the online versionManifest has loaded
      if (!this._manifestPromise) {
        this._manifestPromise = this._loadVersions();
      }
      manifestPromise = this._manifestPromise;
    }
    const locale = window.adobe_dc_sdk.dom ? window.adobe_dc_sdk.dom.getLocale() : 'en-US';
    const addAssets = assets => assets.forEach(a => {
      const url = a.url_template.replace(/{LOCALE}/, locale);
      if (document.head.querySelector(`link[href="${url}"][rel="preload"]`)) {
        // already preloaded
        return;
      }
      const link = document.createElement('link');
      link.href = url;
      // copy all attributes found in the assets definition. e.g. crossorigin
      Object.keys(a).forEach(k => {
        if (k !== 'url_template') link[k] = a[k];
      });
      if (!link.rel) link.rel = 'preload';
      document.head.appendChild(link);
    });

    // Create a bootstrap promise object so that if another bootstrap request comes along
    // before this one is complete, we'll be able to consolidate requests to the same
    // bootstrap
    if (!object.bootstrapPromise) {
      // Dynamically download the code module for the dropin
      object.bootstrapPromise = new Promise(((resolve, reject) => {
        const inject = () => {
          const script = document.createElement('script');
          script.type = 'text/javascript';
          // Use async=false to avoid the browser giving it low resource priority.
          script.async = false;
          script.src = object.url;

          script.onload = () => {
            const bootstrap = window.adobe_dc_sdk.components[object.component];

            resolve(bootstrap);
          };

          script.onerror = () => {
            reject(new Error(`failed to load ${object.url}`));
          };
          document.head.appendChild(script);
        };
        Promise.all([manifestPromise, extrasPromise, spectrumV3CorePromise, mobxPromise]).then(inject, inject);
      }));

      // inject any dependent assets
      const assets = self.groups[object.component].assets || [];
      addAssets(assets);

      Object.keys(self.dropins || []).forEach(d => {
        if (self.dropins[d].component === object.component) {
          self.dropins[d].bootstrapPromise = object.bootstrapPromise;
        }
      });
      // update all the providers sharing the same component
      Object.keys(self.providers || []).forEach(d => {
        if (self.providers[d].component === object.component) {
          self.providers[d].bootstrapPromise = object.bootstrapPromise;
        }
      });
    }

    addAssets(object.assets || []);
    return Promise.all([object.bootstrapPromise, extrasPromise, spectrumV3CorePromise, mobxPromise]).then(([bootstrap]) => {
      // newer/combined dropins/providers have a map of entry points
      try {
        object.bootstrap = bootstrap[category][id];
      } catch (e) {
        clientLogger.error(`Bootstrap error for: "${id}"`);
        throw e;
      }

      return object;
    });
  }

  /**
   * @description
   * This loads the main script module for a provider and returns the class definition
   * If the bootstrap has not yet been loaded, it will be injected first.
   * We then call the bootstrap load() method to get the class definition.
   * A preferred method for loading providers is to use the Provider instance in dc-core/services
   * This will allow deferred loading of providers on first access.
   * @method
   * @param {string} providerId -- the id of the provider as registered in package.json
   * @returns {Promise} - a promise that resolves with the class definition of the provider
   * @public
   */
  loadProviderClass(providerId) {
    return this._loadClass(providerId, 'providers');
  }

  /**
   * @description
   * This loads the main script module for a dropin and returns the class definition
   * If the bootstrap has not yet been loaded, it will be injected first.
   * We then call the bootstrap load() method to get the class definition.
   * @method
   * @param {string} dropinId -- the id of the dropin as registered in package.json
   * @returns {Promise} - a promise that resolves with the class definition of the provider
   * @public
   */
  loadDropinClass(dropinId) {
    return this._loadClass(dropinId, 'dropins');
  }

  /**
   * @description
   * Loads the exported class of the component.
   * @method
   * @param {string} id -- the id of component to load the boostrap for
   * @param {string} category - Category of the component to load bootstrap.
   *    category is either: "dropins" or "providers"
   * @returns {Promise} - a promise that resolves with the class definition of the provider
   * @private
   */
  _loadClass(id, category) {
    // first make sure the bootstrap portion is loaded
    return this._loadBootstrap(id, category).then(
      () => new Promise((resolve, reject) => {
        let loadPromise;

        try {
          const component = this[category][id];
          loadPromise = component.bootstrap.load(component.public_path);
          if (!classesLoaded.includes(id)) {
            // mark this load to help track performance
            // This marker might not add significant new information.  We'll re-evaluate later.
            if (window.performance && window.performance.mark) {
              window.performance.mark(`dc-${id}.load`);
            }

            classesLoaded.push(id);
          }
          loadPromise.then(resolve).catch(reject);
        } catch (e) {
          // This should only occur if the "load" method fails before it can
          // return a Promise.
          reject(e);
        }
      }),
    );
  }
}

// pre-populate these, since the bootstrap loader code relies on the "components"
// section to be available
if (typeof window !== 'undefined') {
  window.adobe_dc_sdk = window.adobe_dc_sdk || {} as any;
  window.adobe_dc_sdk.components = window.adobe_dc_sdk.components || {};
}

export const discovery = new Discovery();
